import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  HealthAndSafety,
  Medication,
  Assignment,
  Science,
  LocalHospital,
  Info,
  CalendarToday,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useQuery } from 'react-query'
import axios from 'axios'
import toast from 'react-hot-toast'

import LoadingSpinner from '../components/LoadingSpinner'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`health-tabpanel-${index}`}
      aria-labelledby={`health-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

interface HealthRecord {
  id?: string
  name: string
  details?: string
  created_at?: string
  [key: string]: any
}

const HealthData: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<HealthRecord | null>(null)
  const [formData, setFormData] = useState<any>({})

  const { data: healthDataResponse, isLoading, refetch } = useQuery(
    'healthData',
    async () => {
      const response = await axios.get('/health-data')
      return response.data.data
    }
  )

  const tabData = [
    {
      label: 'Diseases',
      icon: <HealthAndSafety />,
      data: healthDataResponse?.diseases || [],
      type: 'diseases',
      apiType: 'diseases',
    },
    {
      label: 'Medications',
      icon: <Medication />,
      data: healthDataResponse?.medications || [],
      type: 'medications',
      apiType: 'medications',
    },
    {
      label: 'Symptoms',
      icon: <Assignment />,
      data: healthDataResponse?.symptoms || [],
      type: 'symptoms',
      apiType: 'symptoms',
    },
    {
      label: 'Test Results',
      icon: <Science />,
      data: healthDataResponse?.testResults || [],
      type: 'testResults',
      apiType: 'test-results',
    },
    {
      label: 'Procedures',
      icon: <LocalHospital />,
      data: healthDataResponse?.surgeries || [],
      type: 'surgeries',
      apiType: 'surgeries',
    },
    {
      label: 'Others',
      icon: <Info />,
      data: healthDataResponse?.others || [],
      type: 'others',
      apiType: 'others',
    },
  ]

  const currentTab = tabData[activeTab]

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleAddRecord = () => {
    setEditingRecord(null)
    setFormData({
      name: '',
      details: '',
    })
    setDialogOpen(true)
  }

  const handleEditRecord = (record: HealthRecord) => {
    setEditingRecord(record)
    setFormData({ ...record })
    setDialogOpen(true)
  }

  const handleDeleteRecord = async (id: string, type: string) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await axios.delete(`/health-data/${type}/${id}`)
        toast.success('Record deleted successfully')
        refetch()
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Failed to delete record')
      }
    }
  }

  const handleSaveRecord = async () => {
    try {
      if (editingRecord?.id) {
        // Update existing record
        await axios.put(`/health-data/${currentTab.apiType}/${editingRecord.id}`, formData)
        toast.success('Record updated successfully')
      } else {
        // Create new record
        await axios.post(`/health-data/${currentTab.apiType}`, formData)
        toast.success('Record created successfully')
      }
      setDialogOpen(false)
      refetch()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save record')
    }
  }

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <LoadingSpinner message="Loading your health data..." />
      </Box>
    )
  }

  const renderRecordCard = (record: any) => {
    // Helper to format dates
    const formatDate = (date: string | null) => {
      if (!date) return null
      return dayjs(date).format('MMM D, YYYY')
    }

    return (
      <Grid item xs={12} md={6} key={record.id}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" fontWeight="medium">
                {record.name}
              </Typography>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => handleEditRecord(record)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteRecord(record.id, currentTab.apiType)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {record.details && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {record.details}
              </Typography>
            )}
            {record.results_unit && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Result: {record.results_unit}
              </Typography>
            )}

            {/* Display dates */}
            <Box mt={1.5}>
              {record.dx_date && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Diagnosed: {formatDate(record.dx_date)}
                  </Typography>
                </Box>
              )}
              {record.start_date && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Started: {formatDate(record.start_date)}
                  </Typography>
                </Box>
              )}
              {record.test_date && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Test Date: {formatDate(record.test_date)}
                  </Typography>
                </Box>
              )}
              {record.event_date && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Event Date: {formatDate(record.event_date)}
                  </Typography>
                </Box>
              )}
              {!record.is_ongoing && record.end_date && (
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Ended: {formatDate(record.end_date)}
                  </Typography>
                </Box>
              )}
              {record.dose_unit && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Dose: {record.dose_unit} - {record.frequency}
                </Typography>
              )}
            </Box>

            <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
              {record.is_ongoing && (
                <Chip label="Ongoing" size="small" color="primary" variant="outlined" />
              )}
              {record.is_primary_dx && (
                <Chip label="Primary Diagnosis" size="small" color="secondary" />
              )}
              {record.is_treating_primary_dx && (
                <Chip label="Treating Primary" size="small" color="info" variant="outlined" />
              )}
              {record.abnormal && (
                <Chip label="Abnormal" size="small" color="error" />
              )}
              {record.causes_sae && (
                <Chip label="Causes SAE" size="small" color="warning" />
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" mt={2}>
              Added: {record.created_at ? new Date(record.created_at).toLocaleDateString() : 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    )
  }

  const renderDialog = () => {
    return (
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRecord ? `Edit ${currentTab.label.slice(0, -1)}` : `Add New ${currentTab.label.slice(0, -1)}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Common fields for all types */}
            <TextField
              fullWidth
              label="Name *"
              value={formData.name || ''}
              onChange={(e) => handleFormChange('name', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label={activeTab === 3 ? 'Details / Notes' : 'Details'}
              multiline
              rows={3}
              value={formData.details || ''}
              onChange={(e) => handleFormChange('details', e.target.value)}
              margin="normal"
              placeholder={activeTab === 3 ? 'Add context, reference ranges, or notes about this test result' : undefined}
            />

            {activeTab === 3 && (
              <TextField
                fullWidth
                label="Results / Unit"
                value={formData.results_unit || ''}
                onChange={(e) => handleFormChange('results_unit', e.target.value)}
                margin="normal"
                placeholder="e.g., 120 mg/dL, Positive, Negative"
                helperText="Enter the test result value and unit"
              />
            )}

            {/* Diseases-specific fields */}
            {activeTab === 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>Diagnosis Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Diagnosis Date"
                      value={formData.dx_date ? dayjs(formData.dx_date) : null}
                      onChange={(date) => handleFormChange('dx_date', date ? date.format('YYYY-MM-DD') : null)}
                      disabled={formData.unk_dx_date}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.unk_dx_date || false}
                          onChange={(e) => handleFormChange('unk_dx_date', e.target.checked)}
                        />
                      }
                      label="Unknown diagnosis date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="End Date"
                      value={formData.end_date ? dayjs(formData.end_date) : null}
                      onChange={(date) => handleFormChange('end_date', date ? date.format('YYYY-MM-DD') : null)}
                      disabled={formData.unk_end_date || formData.is_ongoing}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.unk_end_date || false}
                          onChange={(e) => handleFormChange('unk_end_date', e.target.checked)}
                          disabled={formData.is_ongoing}
                        />
                      }
                      label="Unknown end date"
                    />
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_ongoing !== undefined ? formData.is_ongoing : true}
                        onChange={(e) => {
                          handleFormChange('is_ongoing', e.target.checked)
                          if (e.target.checked) {
                            handleFormChange('end_date', null)
                            handleFormChange('unk_end_date', true)
                          }
                        }}
                      />
                    }
                    label="Currently ongoing"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_primary_dx || false}
                        onChange={(e) => handleFormChange('is_primary_dx', e.target.checked)}
                      />
                    }
                    label="Primary diagnosis"
                  />
                </Box>
              </Box>
            )}

            {/* Medications-specific fields */}
            {activeTab === 1 && (
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Dose/Unit"
                      value={formData.dose_unit || ''}
                      onChange={(e) => handleFormChange('dose_unit', e.target.value)}
                      margin="normal"
                      placeholder="e.g., 500mg"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Frequency"
                      value={formData.frequency || ''}
                      onChange={(e) => handleFormChange('frequency', e.target.value)}
                      margin="normal"
                      placeholder="e.g., Twice daily"
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" gutterBottom mt={2}>Dates</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Start Date"
                      value={formData.start_date ? dayjs(formData.start_date) : null}
                      onChange={(date) => handleFormChange('start_date', date ? date.format('YYYY-MM-DD') : null)}
                      disabled={formData.unk_start_date}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.unk_start_date || false}
                          onChange={(e) => handleFormChange('unk_start_date', e.target.checked)}
                        />
                      }
                      label="Unknown start date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="End Date"
                      value={formData.end_date ? dayjs(formData.end_date) : null}
                      onChange={(date) => handleFormChange('end_date', date ? date.format('YYYY-MM-DD') : null)}
                      disabled={formData.unk_end_date || formData.is_ongoing}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.unk_end_date || false}
                          onChange={(e) => handleFormChange('unk_end_date', e.target.checked)}
                          disabled={formData.is_ongoing}
                        />
                      }
                      label="Unknown end date"
                    />
                  </Grid>
                </Grid>

                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_ongoing !== undefined ? formData.is_ongoing : true}
                        onChange={(e) => {
                          handleFormChange('is_ongoing', e.target.checked)
                          if (e.target.checked) {
                            handleFormChange('end_date', null)
                            handleFormChange('unk_end_date', true)
                          }
                        }}
                      />
                    }
                    label="Currently taking"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_treating_primary_dx || false}
                        onChange={(e) => handleFormChange('is_treating_primary_dx', e.target.checked)}
                      />
                    }
                    label="Treating primary diagnosis"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.causes_sae || false}
                        onChange={(e) => handleFormChange('causes_sae', e.target.checked)}
                      />
                    }
                    label="Causes serious adverse events"
                  />
                </Box>

                {formData.causes_sae && (
                  <TextField
                    fullWidth
                    label="SAE Description"
                    multiline
                    rows={2}
                    value={formData.sae_description || ''}
                    onChange={(e) => handleFormChange('sae_description', e.target.value)}
                    margin="normal"
                  />
                )}
              </Box>
            )}

            {/* Symptoms-specific fields */}
            {activeTab === 2 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>Dates</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Start Date"
                      value={formData.start_date ? dayjs(formData.start_date) : null}
                      onChange={(date) => handleFormChange('start_date', date ? date.format('YYYY-MM-DD') : null)}
                      disabled={formData.unk_start_date}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.unk_start_date || false}
                          onChange={(e) => handleFormChange('unk_start_date', e.target.checked)}
                        />
                      }
                      label="Unknown start date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="End Date"
                      value={formData.end_date ? dayjs(formData.end_date) : null}
                      onChange={(date) => handleFormChange('end_date', date ? date.format('YYYY-MM-DD') : null)}
                      disabled={formData.unk_end_date || formData.is_ongoing}
                      slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.unk_end_date || false}
                          onChange={(e) => handleFormChange('unk_end_date', e.target.checked)}
                          disabled={formData.is_ongoing}
                        />
                      }
                      label="Unknown end date"
                    />
                  </Grid>
                </Grid>

                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_ongoing !== undefined ? formData.is_ongoing : true}
                        onChange={(e) => {
                          handleFormChange('is_ongoing', e.target.checked)
                          if (e.target.checked) {
                            handleFormChange('end_date', null)
                            handleFormChange('unk_end_date', true)
                          }
                        }}
                      />
                    }
                    label="Currently experiencing"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_caused_by_primary_dx || false}
                        onChange={(e) => handleFormChange('is_caused_by_primary_dx', e.target.checked)}
                      />
                    }
                    label="Caused by primary diagnosis"
                  />
                </Box>
              </Box>
            )}

            {/* Test Results-specific fields */}
            {activeTab === 3 && (
              <Box mt={2}>
                <DatePicker
                  label="Test Date"
                  value={formData.test_date ? dayjs(formData.test_date) : null}
                  onChange={(date) => handleFormChange('test_date', date ? date.format('YYYY-MM-DD') : null)}
                  disabled={formData.unk_test_date}
                  slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.unk_test_date || false}
                      onChange={(e) => handleFormChange('unk_test_date', e.target.checked)}
                    />
                  }
                  label="Unknown test date"
                />

                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.abnormal || false}
                        onChange={(e) => handleFormChange('abnormal', e.target.checked)}
                      />
                    }
                    label="Abnormal result"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.related_to_primary_dx || false}
                        onChange={(e) => handleFormChange('related_to_primary_dx', e.target.checked)}
                      />
                    }
                    label="Related to primary diagnosis"
                  />
                </Box>
              </Box>
            )}

            {/* Surgeries & Procedures-specific fields */}
            {activeTab === 4 && (
              <Box mt={2}>
                <DatePicker
                  label="Procedure Date"
                  value={formData.event_date ? dayjs(formData.event_date) : null}
                  onChange={(date) => handleFormChange('event_date', date ? date.format('YYYY-MM-DD') : null)}
                  disabled={formData.unk_event_date}
                  slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.unk_event_date || false}
                      onChange={(e) => handleFormChange('unk_event_date', e.target.checked)}
                    />
                  }
                  label="Unknown procedure date"
                />

                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.related_to_primary_dx || false}
                        onChange={(e) => handleFormChange('related_to_primary_dx', e.target.checked)}
                      />
                    }
                    label="Related to primary diagnosis"
                  />
                </Box>
              </Box>
            )}

            {/* Others-specific fields */}
            {activeTab === 5 && (
              <Box mt={2}>
                <DatePicker
                  label="Event Date"
                  value={formData.event_date ? dayjs(formData.event_date) : null}
                  onChange={(date) => handleFormChange('event_date', date ? date.format('YYYY-MM-DD') : null)}
                  disabled={formData.unk_event_date}
                  slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.unk_event_date || false}
                      onChange={(e) => handleFormChange('unk_event_date', e.target.checked)}
                    />
                  }
                  label="Unknown event date"
                />

                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.related_to_primary_dx || false}
                        onChange={(e) => handleFormChange('related_to_primary_dx', e.target.checked)}
                      />
                    }
                    label="Related to primary diagnosis"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveRecord}
            disabled={!formData.name}
          >
            {editingRecord ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Health Data Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRecord}
            sx={{ borderRadius: 2 }}
          >
            Add Record
          </Button>
        </Box>

        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabData.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          {tabData.map((tab, index) => (
            <TabPanel key={index} value={activeTab} index={index}>
              <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                  {tab.label} ({tab.data.length})
                </Typography>
              </Box>

              {tab.data.length === 0 ? (
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      No {tab.label.toLowerCase()} recorded yet
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddRecord}
                      sx={{ mt: 2 }}
                    >
                      Add Your First {tab.label.slice(0, -1)}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Grid container spacing={2}>
                  {tab.data.map((record: any) => renderRecordCard(record))}
                </Grid>
              )}
            </TabPanel>
          ))}
        </Card>

        {/* Add/Edit Dialog */}
        {renderDialog()}
      </Box>
    </LocalizationProvider>
  )
}

export default HealthData

